import { useState, useRef, useEffect } from "react";
import { CoreModal } from "../core/modal/Modal";
import ThemeWrapper from "../theme";
import styles from "./installLockdown.module.css";
import Lottie from "lottie-react";
import rocket from "./images/rocket.json";
import { CoreButton } from "../core/button/Button";
import { CoreTextBox } from "../core/textbox/TextBox";
import Copy from "./images/copy.svg?react";
import Copied from "./images/CheckCircle.svg?react";
import { Button, Link, SvgIcon, Typography } from "@mui/material";
import { coreToast, CoreToastContainer } from "../core/toast";
import i18nPackage from "../i18n/i18n";

export async function determineLockdownDownloadLinkType(): Promise<"mac" | "mac-arm" | "win" | undefined> {
	const userAgentData =
		"userAgentData" in navigator
			? (navigator.userAgentData as
					| {
							getHighEntropyValues?: (info: string[]) => Promise<{ architecture?: string; platform?: string }>;
					  }
					| undefined)
			: undefined;
	if (userAgentData?.getHighEntropyValues) {
		try {
			const platformInfo = await userAgentData.getHighEntropyValues(["architecture"]);
			if (platformInfo.platform?.toLowerCase().includes("win")) {
				return "win";
			} else if (platformInfo.platform?.toLowerCase().includes("mac")) {
				if (platformInfo.architecture?.toLowerCase().includes("arm")) {
					return "mac-arm";
				} else {
					return "mac";
				}
			}
		} catch (e) {
			console.error(e);
		}
	}
	// eslint-disable-next-line deprecation/deprecation
	if (navigator.platform.includes("Mac")) {
		return "mac";
		// eslint-disable-next-line deprecation/deprecation
	} else if (navigator.platform.includes("Win")) {
		return "win";
	}
	return undefined;
}

export function InstallLockdownModalContent({
	quizUrl,
	launchLockdown,
	getDownloadLink,
}: {
	quizUrl: string;
	launchLockdown: () => void;
	getDownloadLink: () => Promise<{ url: string; size?: number } | undefined>;
}) {
	const textInputRef = useRef<HTMLInputElement>(null);
	const [isCopyButtonPressed, setIsCopyButtonPressed] = useState(false);
	const isCopyButtonPressedRef = useRef<boolean>(false);

	const [launchDisabled, setLaunchDisabled] = useState(false);
	const disableLaunchButton = () => {
		setLaunchDisabled(true);
		setTimeout(() => setLaunchDisabled(false), 5000);
	};
	useEffect(() => {
		disableLaunchButton();
	}, []);

	return (
		<div className={styles.dialogContent}>
			<div className={styles.header}>
				<div className={styles.animation}>
					<Lottie animationData={rocket} />
				</div>
				<Typography variant="h2" className={styles.headerText}>
					{i18nPackage.t("installLockdown.tryingToLaunchLockdown")}
				</Typography>
			</div>
			<div className={styles.content}>
				<p className={styles.mainInstructions}>{i18nPackage.t("installLockdown.hasNotLaunched")}</p>
				<div className={styles.section}>
					<ol className={styles.steps} style={{ paddingLeft: "13px" }}>
						<li>
							{i18nPackage.t("installLockdown.downloadAppNow")}
							<CoreButton
								className={styles.stepButton}
								onClick={async () => {
									try {
										const link = (await getDownloadLink())?.url;
										if (link) {
											window.open(link, "_blank");
										} else {
											coreToast.error(i18nPackage.t("installLockdown.windowsOrMacOnlyMsg"));
										}
									} catch (e) {
										console.error(e);
									}
								}}
							>
								{i18nPackage.t("installLockdown.downloadLockdownApp")}
							</CoreButton>
						</li>
						<li>
							{i18nPackage.t("installLockdown.clickLaunchAfterInstallMsg")}
							<CoreButton
								className={styles.stepButton}
								disabled={launchDisabled}
								onClick={() => {
									disableLaunchButton();
									launchLockdown();
								}}
							>
								{launchDisabled
									? i18nPackage.t("installLockdown.launching")
									: i18nPackage.t("installLockdown.launchLockdown")}
							</CoreButton>
						</li>
					</ol>
					<p className={styles.note}>{i18nPackage.t("installLockdown.redirectionNote")}</p>
				</div>
				<div className={styles.or}>or</div>
				<div className={styles.section}>
					{i18nPackage.t("installLockdown.CopyAndInputLinkMsg")}
					<div className={styles.linkRow}>
						<CoreTextBox
							height={30}
							value={quizUrl}
							inputRef={textInputRef}
							style={{ marginLeft: "-1px" }}
							endAdornment={
								<Button
									disableRipple
									sx={{
										backgroundColor: "transparent",
										color: "#0000008c",
										"&:hover": {
											backgroundColor: "transparent",
											color: "#42296e",
										},
										"&:active": {
											backgroundColor: "transparent",
										},
									}}
									onClick={() => {
										if (isCopyButtonPressedRef?.current === false) {
											textInputRef.current?.focus();
											textInputRef?.current?.setSelectionRange(0, quizUrl.length);
											document.execCommand("copy");
											isCopyButtonPressedRef.current = true;
											setIsCopyButtonPressed(true);
											setTimeout(() => {
												setIsCopyButtonPressed(false);
												isCopyButtonPressedRef.current = false;
											}, 3000);
										}
									}}
								>
									{!isCopyButtonPressed ? (
										<SvgIcon>
											<Copy />
										</SvgIcon>
									) : (
										<div
											style={{
												backgroundColor: "#F1F2F4",
												color: "#000000",
												display: "flex",
												alignItems: "center",
												gap: "1px",
												fontSize: "11px",
												borderRadius: "3px",
												paddingRight: "7px",
												paddingLeft: "7px",
												height: "95%",
												textTransform: "none",
											}}
										>
											<Copied /> {i18nPackage.t("installLockdown.copied")}
										</div>
									)}
								</Button>
							}
						></CoreTextBox>
					</div>
				</div>
				<Link className={styles.supportLink} href="https://support.yuja.com/hc/en-us" target="blank">
					{i18nPackage.t("installLockdown.contactSupport")}
				</Link>
			</div>
		</div>
	);
}

export default function InstallLockdownPage({
	quizUrl,
	launchLockdown,
	getDownloadLink,
	onClose,
}: {
	quizUrl: string;
	launchLockdown: () => void;
	getDownloadLink: () => Promise<{ url: string; size?: number } | undefined>;
	onClose?: () => void;
}) {
	const [modalShowing, setModalShowing] = useState(true);

	return (
		<ThemeWrapper>
			<CoreToastContainer />
			<CoreModal
				onClose={() => {
					setModalShowing(false);
					onClose?.();
				}}
				open={modalShowing}
				showBottomCloseButton
			>
				<InstallLockdownModalContent
					quizUrl={quizUrl}
					launchLockdown={launchLockdown}
					getDownloadLink={getDownloadLink}
				/>
			</CoreModal>
		</ThemeWrapper>
	);
}
